import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/onboarding')({
  component: Onboarding,
});

interface OnboardingState {
  answers: Record<string, string>;
  onboardedAt: number | null;
}

async function fetchState(): Promise<OnboardingState> {
  const r = await fetch('/api/onboarding/state', { credentials: 'include' });
  if (!r.ok) throw new Error(`state ${r.status}`);
  return r.json();
}

async function saveAnswer(stepKey: string, value: string): Promise<void> {
  const r = await fetch('/api/onboarding/answer', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ stepKey, value }),
  });
  if (!r.ok) throw new Error(`answer ${r.status}`);
}

async function completeOnboarding(): Promise<void> {
  const r = await fetch('/api/onboarding/complete', {
    method: 'POST',
    credentials: 'include',
  });
  if (!r.ok) throw new Error(`complete ${r.status}`);
}

type StepDef =
  | { key: 'intro'; kind: 'intro' }
  | { key: 'mode'; kind: 'mode' }
  | {
      key: string;
      kind: 'text';
      title: string;
      subtitle?: string;
      placeholder?: string;
      multiline?: boolean;
      whyItMatters?: string;
      modeFlag?: 'deep';
    }
  | { key: string; kind: 'persona'; title: string; subtitle?: string; whyItMatters?: string };

const STEPS: StepDef[] = [
  { key: 'intro', kind: 'intro' },
  { key: 'mode', kind: 'mode' },
  {
    key: 'name',
    kind: 'text',
    title: 'what should I call you?',
    placeholder: 'just a first name is fine',
    whyItMatters:
      "the coach will address you in your own voice — using a name you actually go by makes that less alien.",
  },
  {
    key: 'what_brings_you',
    kind: 'text',
    title: 'what brings you here?',
    subtitle: 'in your own words. one line is fine; a paragraph is fine.',
    multiline: true,
    whyItMatters:
      "this seeds the first version of your profile and tells the system what to listen for in early entries.",
  },
  {
    key: 'persona',
    kind: 'persona',
    title: 'where are you, right now?',
    subtitle: 'choose what fits. you can change this anytime.',
    whyItMatters:
      "persona changes the coach's pacing and which interventions are surfaced. founder unlocks the founder-mode toolkit.",
  },
  {
    key: 'fears',
    kind: 'text',
    title: 'name three fears.',
    subtitle: 'not the big abstract ones. the ones that move you. one per line.',
    multiline: true,
    whyItMatters:
      "the system will quietly notice when entries circle near these. it won't poke at them in the moment — only when there's space.",
  },
  {
    key: 'values',
    kind: 'text',
    title: 'name three things you care about.',
    subtitle: 'one per line.',
    multiline: true,
    whyItMatters:
      "values are the lens the coach uses when reframing — 'what does this look like through {your value}?' goes deeper than generic prompts.",
  },
  {
    key: 'sleep_window',
    kind: 'text',
    title: 'when do you usually sleep?',
    subtitle: 'e.g. "midnight to 7am, badly". approximate is fine.',
    whyItMatters:
      "when biometrics land, the system uses this to spot drift. before then, it shapes when notifications won't fire.",
  },
  {
    key: 'time_patterns',
    kind: 'text',
    title: 'when in the day are you sharpest? when most fragile?',
    subtitle: 'this helps the system meet you at the right time.',
    multiline: true,
    whyItMatters:
      "the coach calibrates how challenging a question to ask based on the time of day you usually use it.",
  },
  // Deep-mode-only steps below.
  {
    key: 'strengths',
    kind: 'text',
    title: 'three things you do better than most.',
    subtitle: "be specific. 'i'm good with people' is too broad.",
    multiline: true,
    whyItMatters:
      "this is the seed for the proof ledger — when imposter thoughts surface, the system has evidence to point at.",
    modeFlag: 'deep',
  },
  {
    key: 'signature',
    kind: 'text',
    title: "what's the thing people remember about you?",
    subtitle: 'the trait, the move, the way of being. not what you do for a living.',
    whyItMatters:
      "your signature is more durable than your job. the narrative practice draws from this when you reach 'what you became'.",
    modeFlag: 'deep',
  },
  {
    key: 'future_self',
    kind: 'text',
    title: 'three years from now, what does sunday morning look like?',
    subtitle: 'not the achievement. the texture of the day.',
    multiline: true,
    whyItMatters:
      "futures stated as textures (not goals) move differently. the coach uses these as orientation, never as performance metrics.",
    modeFlag: 'deep',
  },
  {
    key: 'energy_patterns',
    kind: 'text',
    title: 'what gives you energy? what costs you energy?',
    subtitle: 'two short lists.',
    multiline: true,
    whyItMatters:
      "when state-classifier sees 'high cognitive load', the system can suggest moves from your energy list instead of generic advice.",
    modeFlag: 'deep',
  },
  {
    key: 'limiting_belief',
    kind: 'text',
    title: 'one belief about yourself you suspect is wrong.',
    subtitle: 'a story you tell that no longer fits.',
    multiline: true,
    whyItMatters:
      "the fear-shape classifier reads entries against this. when you slide back into that belief, the coach can name it.",
    modeFlag: 'deep',
  },
  {
    key: 'standing_offer',
    kind: 'text',
    title: 'what can you offer your former self today?',
    subtitle: 'the version of you who didn\'t know what you know now.',
    multiline: true,
    whyItMatters:
      "this seeds the forgiveness arc with a closing line — the kind of thing you'd write at the end of a letter.",
    modeFlag: 'deep',
  },
  {
    key: 'one_thing',
    kind: 'text',
    title: "one thing you'd want the system to never forget.",
    subtitle: 'a phrase, a person, a date, a promise.',
    whyItMatters:
      "this gets pinned to your profile as a personalisation directive — every coach turn honours it.",
    modeFlag: 'deep',
  },
];

const PERSONA_OPTIONS: Array<{ id: string; label: string; description: string }> = [
  { id: 'founder', label: 'Founder', description: 'time and identity have collapsed into each other.' },
  { id: 'human:rebuilding', label: 'Rebuilding', description: 'coming back from something.' },
  { id: 'human:becoming', label: 'Becoming', description: 'building confidence, claiming space.' },
  { id: 'human:forgiving', label: 'Forgiving', description: 'working through guilt, shame, regret.' },
  { id: 'human:loving', label: 'Loving', description: 'practising self-compassion.' },
  { id: 'human:steady', label: 'Steady', description: 'well; here for maintenance and depth.' },
  { id: 'human:custom', label: 'Custom', description: "I'll describe myself in my own words." },
];

function visibleSteps(mode: 'quick' | 'deep'): StepDef[] {
  return STEPS.filter((s) => {
    if (s.kind === 'text' && s.modeFlag === 'deep' && mode === 'quick') return false;
    return true;
  });
}

function Onboarding() {
  const nav = useNavigate();
  const state = useQuery({ queryKey: ['onboarding-state'], queryFn: fetchState });
  const [stepIndex, setStepIndex] = useState(0);
  const [value, setValue] = useState('');
  const [mode, setMode] = useState<'quick' | 'deep'>('quick');
  const [whyOpen, setWhyOpen] = useState(false);

  const steps = visibleSteps(mode);
  const step = steps[stepIndex];

  useEffect(() => {
    if (state.data?.answers.mode === 'deep') setMode('deep');
  }, [state.data?.answers.mode]);

  useEffect(() => {
    if (!step || step.kind === 'intro' || step.kind === 'mode') {
      setValue('');
      setWhyOpen(false);
      return;
    }
    setValue(state.data?.answers[step.key] ?? '');
    setWhyOpen(false);
  }, [stepIndex, state.data, step]);

  const answerMutation = useMutation({
    mutationFn: ({ k, v }: { k: string; v: string }) => saveAnswer(k, v),
  });
  const completeMutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => nav({ to: '/today' }),
  });

  if (!step) return null;

  const onNext = async (skip: boolean) => {
    if (step.kind === 'mode') {
      await answerMutation.mutateAsync({ k: 'mode', v: mode });
    } else if (step.kind !== 'intro') {
      const v = skip ? '' : value.trim();
      if (v) await answerMutation.mutateAsync({ k: step.key, v });
    }
    if (stepIndex === steps.length - 1) {
      await completeMutation.mutateAsync();
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const onBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const why = step.kind !== 'intro' && step.kind !== 'mode' ? step.whyItMatters : undefined;

  return (
    <section className="mx-auto max-w-xl px-4 py-16">
      <div className="text-xs text-muted-foreground">
        {stepIndex + 1} of {steps.length}
      </div>

      {step.kind === 'intro' && (
        <div className="mt-6">
          <h1 className="text-2xl font-semibold tracking-tight">a few questions, briefly.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            none of this is shared. every step is skippable. you can change any of it later.
          </p>
          <div className="mt-8">
            <Button onClick={() => onNext(false)}>begin</Button>
          </div>
        </div>
      )}

      {step.kind === 'mode' && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold tracking-tight">how deep do you want to go?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            pick one. you can come back and do the longer version later.
          </p>
          <div className="mt-6 grid gap-2">
            <button
              onClick={() => setMode('quick')}
              className={`text-left rounded-[var(--radius)] border p-4 transition ${mode === 'quick' ? 'border-foreground' : 'border-border hover:bg-muted'}`}
            >
              <div className="font-medium">quick · 8 questions · ~2 min</div>
              <div className="text-sm text-muted-foreground">
                enough for the system to start. the profile fills in as you write.
              </div>
            </button>
            <button
              onClick={() => setMode('deep')}
              className={`text-left rounded-[var(--radius)] border p-4 transition ${mode === 'deep' ? 'border-foreground' : 'border-border hover:bg-muted'}`}
            >
              <div className="font-medium">deeper · 15 questions · ~10 min</div>
              <div className="text-sm text-muted-foreground">
                seven extra prompts — strengths, signature, future, beliefs, energy. seeds the profile + proof ledger + practices.
              </div>
            </button>
          </div>
          <div className="mt-8 flex gap-2">
            <Button variant="ghost" disabled={stepIndex === 0} onClick={onBack}>
              back
            </Button>
            <Button className="ml-auto" onClick={() => onNext(false)}>
              continue
            </Button>
          </div>
        </div>
      )}

      {step.kind === 'text' && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold tracking-tight">{step.title}</h2>
          {step.subtitle && <p className="mt-2 text-sm text-muted-foreground">{step.subtitle}</p>}
          {step.multiline ? (
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={step.placeholder}
              rows={5}
              className="mt-6 w-full rounded-[var(--radius)] border border-input bg-background p-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          ) : (
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={step.placeholder}
              className="mt-6 w-full h-11 rounded-[var(--radius)] border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          )}

          {why && (
            <div className="mt-3 text-xs">
              <button
                type="button"
                onClick={() => setWhyOpen((v) => !v)}
                className="text-muted-foreground hover:text-foreground underline"
              >
                {whyOpen ? 'hide' : 'why this matters'}
              </button>
              {whyOpen && (
                <p className="mt-2 text-muted-foreground leading-relaxed">{why}</p>
              )}
            </div>
          )}

          <Controls
            backDisabled={stepIndex === 0}
            onBack={onBack}
            onSkip={() => onNext(true)}
            onNext={() => onNext(false)}
            disabled={answerMutation.isPending || completeMutation.isPending}
          />
        </div>
      )}

      {step.kind === 'persona' && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold tracking-tight">{step.title}</h2>
          {step.subtitle && <p className="mt-2 text-sm text-muted-foreground">{step.subtitle}</p>}
          <div className="mt-6 grid gap-2">
            {PERSONA_OPTIONS.map((p) => (
              <button
                key={p.id}
                onClick={() => setValue(p.id)}
                className={`text-left rounded-[var(--radius)] border p-4 transition ${value === p.id ? 'border-foreground' : 'border-border hover:bg-muted'}`}
              >
                <div className="font-medium">{p.label}</div>
                <div className="text-sm text-muted-foreground">{p.description}</div>
              </button>
            ))}
          </div>
          {step.whyItMatters && (
            <div className="mt-3 text-xs">
              <button
                type="button"
                onClick={() => setWhyOpen((v) => !v)}
                className="text-muted-foreground hover:text-foreground underline"
              >
                {whyOpen ? 'hide' : 'why this matters'}
              </button>
              {whyOpen && (
                <p className="mt-2 text-muted-foreground leading-relaxed">{step.whyItMatters}</p>
              )}
            </div>
          )}
          <Controls
            backDisabled={stepIndex === 0}
            onBack={onBack}
            onSkip={() => onNext(true)}
            onNext={() => onNext(false)}
            disabled={answerMutation.isPending || completeMutation.isPending}
          />
        </div>
      )}

      <div className="mt-6 min-h-5 text-sm text-muted-foreground">
        {answerMutation.isPending && 'saving…'}
        {completeMutation.isPending && 'finishing…'}
      </div>
    </section>
  );
}

function Controls(props: {
  backDisabled: boolean;
  onBack: () => void;
  onSkip: () => void;
  onNext: () => void;
  disabled: boolean;
}) {
  return (
    <div className="mt-8 flex items-center gap-3">
      <Button variant="ghost" disabled={props.backDisabled || props.disabled} onClick={props.onBack}>
        back
      </Button>
      <Button variant="ghost" disabled={props.disabled} onClick={props.onSkip}>
        skip
      </Button>
      <Button className="ml-auto" disabled={props.disabled} onClick={props.onNext}>
        next
      </Button>
    </div>
  );
}
